import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { logout, getItem } from "../../utils/authStorage";
import { parseJwt } from "../../utils/jwtDecoder";
import { fetchAlertsList, fetchSensorAlerts } from "../../services/alertService";
import { fetchDeviceList } from "../../services/deviceService";
import { useActiveAlerts } from "../../contexts/ActiveAlertsContext";
import { relativeTimeFrom } from "../../utils/helpers";
import {
  ShieldIcon,
  TablerAlertTriangle,
  TablerX,
} from "../shared/Icons";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import "./DashboardPage.css";
import { useAlertWebSocket, useDeviceWebSocket } from "../../contexts/WebSocketContext";
import ActiveAlertCard from "./ActiveAlertCard";
import AlertDetailPage from "./AlertDetailPage";

function DashboardPage() {
  const { isDarkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [alertVisible, setAlertVisible] = useState(true);
  const { isConnected: alertsWsConnected } = useAlertWebSocket();
  const { isConnected: devicesWsConnected, subscribe: subscribeDevices } = useDeviceWebSocket();
  const wsConnected = alertsWsConnected || devicesWsConnected;

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAlertId = searchParams.get("alertId");
  const selectedAlertType = searchParams.get("type");

  const { activeAlerts } = useActiveAlerts();
  const [lastProcessedText, setLastProcessedText] = useState("N/A");
  const [devices, setDevices] = useState([]);

  async function loadLastProcessed() {
    try {
      const [cameraData, sensorData] = await Promise.all([fetchAlertsList(), fetchSensorAlerts()]);
      const times = [];
      (cameraData || []).forEach(a => a.start_time && times.push(a.start_time));
      (sensorData || []).forEach(s => s.timestamp && times.push(s.timestamp));
      if (times.length === 0) {
        setLastProcessedText("N/A");
      } else {
        const latest = times.reduce((a, b) => new Date(a) > new Date(b) ? a : b);
        setLastProcessedText(relativeTimeFrom(latest));
      }
    } catch (e) {
      console.error("Failed to load alert data:", e);
      setLastProcessedText("N/A");
    }
  }

  async function loadDeviceData() {
    try {
      const data = await fetchDeviceList();
      setDevices(data || []);
    } catch (e) {
      console.error("Failed to load device data:", e);
    }
  }

  useEffect(() => {
    loadLastProcessed();
    loadDeviceData();
  }, [wsConnected]);

  // Subscribe to real-time device updates
  useEffect(() => {
    const unsub = subscribeDevices((raw) => {
      const type = raw?.type;
      if (type === "device.status.active" || type === "device.status.inactive" || type === "device.status.streaming") {
        loadDeviceData();
      }
    });
    return unsub;
  }, [subscribeDevices]);

  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!selectedAlertId) {
      loadLastProcessed();
      loadDeviceData();
    }
  }, [selectedAlertId]);





  const token = getItem("accessToken");
  const tokenPayload = token ? parseJwt(token) : null;
  const username = getItem("username") || tokenPayload?.username || tokenPayload?.preferred_username || "User";
  const rawRole = getItem("role") || tokenPayload?.role || tokenPayload?.roles?.[0] || "Operator";
  const role = rawRole.toLowerCase();

  const handleLogout = () => {
    logout();
  };

  const navigate = useNavigate();
  const totalDevices = devices.length;
  const onlineDevices = devices.filter(
    (d) => d.status === "ACTIVE" || d.status === "STREAMING"
  ).length;

  if (selectedAlertId) {
    return (
      <AlertDetailPage
        alert={{ id: selectedAlertId, type: selectedAlertType }}
        onClose={() => {
          if (window.history && window.history.length > 1) {
            navigate(-1);
          } else {
            setSearchParams({});
          }
        }}
      />
    );
  }

  return (
    <div className={`dashboard-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(prev => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className="main-content">
        {/* Top Navigation Bar */}
        <Header title="Dashboard" />

        {/* Main divider */}
        <div className="main-divider" />

        {/* Dashboard Content */}
        <div className="dashboard-content">
          {/* Connection Status */}
          <div className="status-bar">
            <span className="status-text">
              WebSocket: <span className={wsConnected ? "status-connected" : "status-disconnected"}>{wsConnected ? "connected" : "disconnected"}</span>
            </span>
            <div className="status-bar-right">
              <span className="status-text status-label">Last alert processed</span>
              <span className="status-text ts-muted">{lastProcessedText}</span>
            </div>
          </div>

          {/* Device Offline Alert */}
          {alertVisible && onlineDevices === 0 && totalDevices > 0 && (
            <div className="alert-bar alert-warning">
              <TablerAlertTriangle color="#FF8D89" size={24} />
              <span className="alert-text">
                <strong>All {totalDevices} devices offline.</strong> No camera or drone is currently streaming monitoring coverage is degraded.
              </span>
              <button className="icon-btn" onClick={() => setAlertVisible(false)}>
                <TablerX color="#FF8D89" size={18} />
              </button>
            </div>
          )}

          {/* Overview Cards */}
          <div className="overview-cards">
            <div className="overview-card">
              <div className="card-label">Active Alerts</div>
              <div className="card-value">{activeAlerts.length}</div>
            </div>
            <div className="overview-card">
              <div className="card-label">Device reporting</div>
              <div className="card-value">
                <span className={onlineDevices === 0 ? "value-danger" : ""}>{onlineDevices}</span>
                <span className="value-muted"> of {totalDevices}</span>
              </div>
            </div>
          </div>

          {/* Active Alerts Section */}
          {activeAlerts.length > 0 ? (
            <div className="active-alerts-section">
              <h3 className="section-title">Active Alerts</h3>
              <div className="dashboard-alerts-card-list">
                {activeAlerts.slice(0, 6).map((alert, index) => (
                  <ActiveAlertCard
                    key={alert.id || index}
                    alert={alert}
                    formatDateTime={(isoString) => {
                      if (!isoString) return "N/A";
                      const d = new Date(isoString);
                      const dd = String(d.getDate()).padStart(2, "0");
                      const mm = String(d.getMonth() + 1).padStart(2, "0");
                      const yyyy = d.getFullYear();
                      let hh = d.getHours();
                      const min = String(d.getMinutes()).padStart(2, "0");
                      const ampm = hh >= 12 ? "PM" : "AM";
                      hh = hh % 12 || 12;
                      return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
                    }}
                    setSearchParams={setSearchParams}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="threat-status">
              <div className="threat-icon-bg">
                <ShieldIcon color="#00AF64" size={28} />
              </div>
              <h2 className="threat-title">No active threats</h2>
              <p className="threat-subtitle">
                System is monitoring continuously. You'll be alerted the moment something is detected.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default DashboardPage;